/*
 * NOTCH: Anchor scroll helper
 * Robust same-page + cross-page scrolling for "Anchor Link by Notch" sections.
 * Scoped strictly to `.notch-anchor-link` targets so it never interferes with
 * normal links, product/gallery anchors, buttons, or the Shopify editor.
 *
 * Update-safe: lives in assets/ and is loaded from the Notch-owned section file.
 */
(function () {
  if (window.__notchAnchorScrollLoaded) return;
  window.__notchAnchorScrollLoaded = true;

  var HEADER_FALLBACK = 80; // px, matches CSS fallback (8rem)
  var EXTRA_GAP = 8; // px breathing room below the header

  // Measure the real sticky/fixed header height and publish it as --header-height
  // so the anchors' CSS scroll-margin-top is accurate even before any menu hover.
  function measureHeaderOffset() {
    var total = 0;
    var groups = document.querySelectorAll('.shopify-section-group-header-group');

    if (groups.length) {
      groups.forEach(function (group) {
        var pos = window.getComputedStyle(group).position;
        // Only header groups that overlap content (sticky/fixed) need an offset.
        if (pos === 'sticky' || pos === 'fixed' || group.querySelector('sticky-header')) {
          total += group.offsetHeight;
        }
      });
    }

    if (!total) {
      var header = document.querySelector('sticky-header, .header-wrapper, .shopify-section-header');
      if (header) total = header.offsetHeight;
    }

    if (total > 0) {
      document.documentElement.style.setProperty('--header-height', total + 'px');
    }

    return total || HEADER_FALLBACK;
  }

  function getAnchorTarget(hash) {
    if (!hash || hash.length < 2) return null;
    var id;
    try {
      id = decodeURIComponent(hash.slice(1));
    } catch (e) {
      id = hash.slice(1);
    }
    if (!id) return null;

    var el = document.getElementById(id);
    if (!el) return null;

    // Only manage our own anchor elements; leave everything else to the browser.
    if (el.classList.contains('notch-anchor-link')) return el;
    if (el.closest && el.closest('.notch-anchor-link-section')) return el;
    return null;
  }

  function computeTop(el) {
    var offset = measureHeaderOffset();
    // Respect a per-anchor scroll-margin-top if it resolves larger than the header.
    var smt = parseFloat(window.getComputedStyle(el).scrollMarginTop) || 0;
    var applied = Math.max(offset + EXTRA_GAP, smt);
    var top = el.getBoundingClientRect().top + window.pageYOffset - applied;
    return Math.max(top, 0);
  }

  function scrollToEl(el, smooth) {
    if (!el) return;
    window.scrollTo({ top: computeTop(el), behavior: smooth ? 'smooth' : 'auto' });
  }

  // On initial load layout keeps shifting (lazy images, reveal animations).
  // Re-correct the position several times until it stabilizes.
  function scrollToHashOnLoad() {
    var el = getAnchorTarget(window.location.hash);
    if (!el) return;

    if ('scrollRestoration' in history) {
      try { history.scrollRestoration = 'manual'; } catch (e) {}
    }

    var attempts = 0;
    var lastTop = -1;
    var stableCount = 0;

    function attempt() {
      el = getAnchorTarget(window.location.hash) || el;
      if (!el) return;

      var top = computeTop(el);
      window.scrollTo({ top: top, behavior: 'auto' });

      if (Math.abs(top - lastTop) < 2) {
        stableCount++;
      } else {
        stableCount = 0;
      }
      lastTop = top;
      attempts++;

      // Stop once the target position has been stable twice, or after the budget.
      if (stableCount < 2 && attempts < 14) {
        setTimeout(function () {
          requestAnimationFrame(attempt);
        }, attempts < 6 ? 60 : 200);
      }
    }

    requestAnimationFrame(attempt);
  }

  // Same-page smooth scrolling for clicks on links pointing at our anchors.
  function onClick(event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    var link = event.target.closest && event.target.closest('a[href]');
    if (!link) return;

    var href = link.getAttribute('href');
    if (!href) return;

    var hashIndex = href.indexOf('#');
    if (hashIndex < 0) return;

    var hash = href.slice(hashIndex);
    if (hash.length < 2) return;

    // Only treat as same-page when the path before the hash matches the current page.
    var path = href.slice(0, hashIndex);
    var samePage =
      path === '' ||
      path === '#' ||
      path === window.location.pathname ||
      path === window.location.origin + window.location.pathname ||
      path === window.location.href.split('#')[0];

    if (!samePage) return; // cross-page link: let it navigate; target page handles scroll on load

    var el = getAnchorTarget(hash);
    if (!el) return; // not one of our anchors: do nothing, keep native behavior

    event.preventDefault();
    if (window.history && history.pushState) {
      history.pushState(null, '', hash);
    } else {
      window.location.hash = hash;
    }
    scrollToEl(el, true);
  }

  function onHashChange() {
    scrollToEl(getAnchorTarget(window.location.hash), true);
  }

  function init() {
    measureHeaderOffset();
    scrollToHashOnLoad();
  }

  document.addEventListener('click', onClick, false);
  window.addEventListener('hashchange', onHashChange, false);
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) scrollToHashOnLoad();
  });

  if (document.readyState === 'complete') {
    init();
  } else {
    window.addEventListener('load', init);
  }
})();
